import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Drama, Check, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";

export const fmtPts = (p) => `${Number(p ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}`;

function RankLadder({ ranks, total }) {
  return (
    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="thespian-rank-ladder">
      {ranks.map((r) => {
        const done = total >= r.threshold;
        return (
          <div key={r.name} className={`rounded-lg border px-3 py-2 transition-colors ${done ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid={`thespian-ladder-${r.threshold}`}>
            <p className={`flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider ${done ? "text-amber-700" : "text-slate-400"}`}>
              {done && <Check className="h-3 w-3" />}{r.threshold}+ pts
            </p>
            <p className={`text-xs font-semibold leading-tight ${done ? "text-amber-900" : "text-slate-500"}`}>{r.name}</p>
          </div>
        );
      })}
    </div>
  );
}

// Current ITS rank, cumulative points, progress to the next rank. `compact` = dashboard link card.
export function ThespianRankCard({ summary, compact = false, to = "/member/thespian", className = "mt-4" }) {
  const { total_points: total = 0, rank, next_rank: next, next_threshold: nextAt, points_to_next: toGo, ranks = [] } = summary;
  const prevAt = [...ranks].reverse().find((r) => total >= r.threshold)?.threshold || 0;
  const pct = nextAt ? Math.min(100, Math.round(((total - prevAt) / (nextAt - prevAt)) * 100)) : 100;
  const body = (
    <Card className={`relative overflow-hidden border-slate-200 bg-white shadow-sm ${compact ? `${className} p-5 transition-shadow hover:shadow-md` : "p-6"}`} data-testid={compact ? "thespian-dashboard-card" : "thespian-rank-card"}>
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 ring-1 ring-amber-200" data-testid="thespian-icon-tile"><Drama className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">ITS Rank</p>
          <p className="mt-0.5 font-heading text-2xl font-extrabold text-slate-900" data-testid="thespian-rank-name">{rank || "Not yet inducted"}</p>
          <p className="mt-1 text-sm text-slate-600"><span className="font-bold tabular-nums text-slate-900" data-testid="thespian-total-points">{fmtPts(total)}</span> cumulative points</p>
        </div>
        {compact && <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />}
      </div>
      <div className="mt-4">
        <div className="h-2 overflow-hidden rounded-full bg-amber-100"><div className="h-full rounded-full bg-amber-500 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
        <p className="mt-1.5 text-xs text-slate-500" data-testid="thespian-next-rank">
          {next ? <><span className="font-semibold tabular-nums text-slate-700">{fmtPts(toGo)}</span> more point{toGo === 1 ? "" : "s"} to {next}</> : "Highest ITS rank reached — bravo!"}
        </p>
      </div>
      {!compact && <RankLadder ranks={ranks} total={total} />}
    </Card>
  );
  return compact ? <Link to={to} className="block h-full" data-testid="thespian-dashboard-link">{body}</Link> : body;
}

// Self-loading wrapper. `requireApproved` hides it until the member has ≥1 approved claim.
export default function ThespianRankWidget({ compact, requireApproved }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/thespian/my").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s?.enabled || (requireApproved && !s.approved_count)) return null;
  return <ThespianRankCard summary={s} compact={compact} />;
}
